import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  SubmitHandler,
  useForm,
  Controller,
  FormProvider,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useConfirm } from 'components/ConfirmDialog';
import { Map } from 'features/mapping/components/Map';
import { MapType } from 'features/mapping/mapTypePolicy';
import { getHazardEventIcon, getHazardTypeIcon } from 'features/mapping/icons';
import { Hazard } from 'features/hazards/hazard.types';
import { useCacheState } from 'state/useCacheState';
import { RelatedWildfire } from 'features/wildfires/wildfireGeoEventData.types';
import { RelatedFlooding } from 'features/flooding/flooding.types';
import { FormLinks } from 'components/FormLinks';
import { useIosKeyboardScroller } from 'hooks/useIosKeyboardScroller';
import { useGeoEventAutocomplete } from 'features/geoEvents/hooks/useGeoEventAutocomplete';
import { enqueueSnackbar } from 'notistack';
import { FormWithoutEnter } from 'components/FormWithoutEnter';
import { HAZARD_TYPE_TO_TRANS_KEY } from 'features/hazards/hazard.constants';
import { useUserRegionsQuery } from 'hooks/useUserRegionsQuery';
import { useSyncInitialLocationToFormState } from 'features/geoEvents/hooks/useSyncInitialLocationToFormState';
import { useFormBlocker } from 'hooks/useFormBlocker';
import { useGeoEventEditMarker } from 'features/geoEvents/hooks/useGeoEventEditMarker';
import { useMarkerEditLock } from 'features/geoEvents/hooks/useMarkerEditLock';
import { useMarkerMoveConfirmation } from 'features/geoEvents/hooks/useMarkerMoveConfirmation';
import { iconFootprint } from 'features/mapping/components/MarkerEditConnectorLine';
import { MarkerEditControls } from 'features/mapping/components/MarkerEditControls';
import { MarkerEditGhost } from 'features/mapping/components/MarkerEditGhost';
import {
  getRedundancyMessage,
  getViewGeoEventLink,
} from 'features/geoEvents/geoEvent.utils';
import { MapFormLayout } from 'components/MapFormLayout';
import { useDefaultMapCenter } from 'hooks/useDefaultMapCenter';
import { useLatLngFromQueryParams } from 'hooks/useLatLngFromQueryParams';
import { useInitialMapZoom } from 'hooks/useInitialMapZoom';
import { useQueryParams } from 'hooks/useQueryParams';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { NotificationType } from 'features/notifications/notifications.types';
import { NotificationType as NotificationTypeConstant } from 'features/notifications/notifications.constants';
import { ReportMessageField } from 'features/reports/components/ReportMessageField';
import { MediaForm, isMediaAlertCamera } from 'components/MediaForm';
import {
  ImageUploadData,
  useImageUpload,
} from 'features/reports/hooks/useImageUpload';
import { AlertCamerasLayer } from 'features/mapping/components/layers/AlertCamerasLayer';
import { HttpStatusCode } from 'axios';
import { AutocompleteWithBackdrop } from 'components/AutocompleteWithBackdrop';
import { Loader } from 'components/Loader';
import {
  getInitialValues,
  hazardFormDataToServerData,
  postHazard,
  updateHazard,
} from './HazardForm.utils';
import { HazardFormValues, HazardType } from './HazardForm.types';
import { useStyles } from './HazardForm.styles';
import { hazardTypes, statusOptions } from './HazardForm.constants';
import { getValidationSchema } from './HazardForm.validation';
import { GeoEventTypes } from '../../../../constants';

type HazardFormProps = {
  hazard?: Hazard;
  parentGeoEventId?: number;
  parentGeoEventType?: 'wildfire' | 'flooding';
};

type HazardMarkerProps = {
  hazard: Partial<Hazard>;
  onChange: (location: { lat: number; lng: number }) => void;
  locked?: boolean;
};

const HazardMarker = (props: HazardMarkerProps): JSX.Element | null => {
  const { hazard, onChange, locked = false } = props;
  const { markerProps } = useGeoEventEditMarker({
    geoEventType: GeoEventTypes.hazard,
    onChange,
    disabled: locked,
  });

  return (
    <Map.HazardGeoEventMarker
      {...markerProps}
      geoEvent={hazard}
      draggable={!locked}
    />
  );
};

export const HazardForm = (props: HazardFormProps): JSX.Element => {
  const { hazard, parentGeoEventId, parentGeoEventType } = props;
  const { lat: initialLatitude, lng: initialLongitude } =
    useLatLngFromQueryParams();
  const initialZoom = useInitialMapZoom();
  const isInternalTabEnabled = useReleaseFlag('internal_tab_on_incidents');
  const [{ alertCameraId }] = useQueryParams<{
    alertCameraId?: string;
  }>();
  const { classes } = useStyles();
  const { t } = useTranslation();
  const defaultMapCenter = useDefaultMapCenter({
    geoEvent: hazard,
    initialLat: initialLatitude,
    initialLng: initialLongitude,
  });
  const { setCacheState } = useCacheState();
  const confirm = useConfirm();

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  useIosKeyboardScroller(scrollContainerRef);

  const { regions: regionOptions, isLoading: isLoadingRegions } =
    useUserRegionsQuery();

  const createHazardMutation = useMutation({ mutationFn: postHazard });

  const updateHazardMutation = useMutation({ mutationFn: updateHazard });

  const validationSchema = useMemo(
    () => getValidationSchema(t, hazard),
    [t, hazard],
  );

  const initialValues: HazardFormValues = useMemo(
    () =>
      getInitialValues({
        hazard,
        latitude: initialLatitude,
        longitude: initialLongitude,
        initialAlertCameraId: hazard ? undefined : (alertCameraId ?? undefined),
      }),
    [initialLatitude, initialLongitude, hazard, alertCameraId],
  );

  const formMethods = useForm<HazardFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
    values: initialValues, // will update form values when initialValues change (hazard updates)
    mode: 'onBlur',
  });

  const { control, formState, handleSubmit, setValue, watch } = formMethods;

  const { unblockedNavigate } = useFormBlocker({
    shouldBlockNavigation: Boolean(formState.isDirty),
  });

  const { handleLocationChange } = useSyncInitialLocationToFormState({
    isLoadingRegions,
    regionOptions,
    geoEventType: GeoEventTypes.hazard,
    geoEvent: hazard,
    setValue,
  });

  const [type, status, lat, lng, parentGeoEvents, media, fileType] = watch([
    'type',
    'status',
    'lat',
    'lng',
    'parentGeoEvents',
    'media',
    'fileType',
  ]);

  // Image upload for initial report (only on create)
  const imgData = useMemo((): ImageUploadData | null => {
    if (!media || !('type' in media) || !fileType) return null;
    return { file: media, fileType };
  }, [fileType, media]);

  const handleImageUploadError = useCallback(() => {
    enqueueSnackbar(t('addIncidentReport.imageUploadError'), {
      variant: 'error',
    });
  }, [t]);

  const { mediaUrl, error: uploadImgError } = useImageUpload({
    imgData,
    onError: handleImageUploadError,
    enabled: !hazard,
  });

  const handleSubmitSuccess = (createdHazardId?: number): void => {
    const isUpdate = Boolean(hazard?.id);

    const message = isUpdate
      ? t('createEditHazard.successMessage.update')
      : t('createEditHazard.successMessage.create');

    enqueueSnackbar(message, { variant: 'success' });

    if (isUpdate) {
      setCacheState(null);
    }

    let route = '/';
    if (isUpdate) {
      route = `/i/${hazard!.id}`;
    } else if (parentGeoEventId && parentGeoEventType) {
      route = getViewGeoEventLink(parentGeoEventType, parentGeoEventId);
    } else if (createdHazardId) {
      route = `/i/${createdHazardId}`;
    }

    unblockedNavigate(route);
  };

  const handleNoIncidentConfirmation = async (): Promise<boolean> => {
    const resp = await confirm(t('createEditHazard.confirmNoIncident'));
    return resp;
  };

  const handleSubmitHazard: SubmitHandler<HazardFormValues> = async (
    values,
  ) => {
    // Check if no incident is linked and ask for confirmation
    if (values.parentGeoEvents.length === 0 && !hazard?.id) {
      const confirmation = await handleNoIncidentConfirmation();
      if (!confirmation) {
        return;
      }
    }

    const data = hazardFormDataToServerData(values, mediaUrl);
    if (!data) return;

    try {
      let createdHazardId: number | undefined;

      if (hazard?.id) {
        await updateHazardMutation.mutateAsync({ id: hazard.id, data });
      } else {
        const response = await createHazardMutation.mutateAsync({ data });
        createdHazardId = response.data.id;
      }

      handleSubmitSuccess(createdHazardId);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const errorStatus = error?.response?.status;

      if (errorStatus === HttpStatusCode.Conflict) {
        const shouldRetry = await confirm(getRedundancyMessage(error.response));

        if (shouldRetry) {
          try {
            const response = await createHazardMutation.mutateAsync({
              data,
              skipRedundancyCheck: true,
            });

            handleSubmitSuccess(response.data.id);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (finalError: any) {
            enqueueSnackbar(finalError.message, { variant: 'error' });
          }
        }
      } else if (errorStatus === HttpStatusCode.Forbidden) {
        enqueueSnackbar(t('createEditHazard.errors.forbidden'), {
          variant: 'error',
        });
      } else if (errorStatus === HttpStatusCode.BadRequest) {
        enqueueSnackbar(t('createEditHazard.errors.badRequest'), {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(error.message, { variant: 'error' });
      }
    }
  };

  const { muiAutocompleteProps: geoEventAutocompleteProps } =
    useGeoEventAutocomplete<RelatedWildfire | RelatedFlooding>({
      enabled: true,
      latLng: initialValues,
      value: parentGeoEvents,
      geoEventTypes: ['flooding'],
    });

  useEffect(() => {
    if (!parentGeoEventId || parentGeoEvents.length > 0) {
      return;
    }

    const initialParentGeoEvent = geoEventAutocompleteProps.options.find(
      (geoEvent) => geoEvent.id === parentGeoEventId,
    );

    if (initialParentGeoEvent) {
      setValue('parentGeoEvents', [initialParentGeoEvent], {
        shouldDirty: false,
      });
    }
  }, [
    geoEventAutocompleteProps.options,
    parentGeoEventId,
    parentGeoEvents.length,
    setValue,
  ]);

  const markerLock = useMarkerEditLock({
    onCommit: handleLocationChange,
    isEditing: Boolean(hazard),
  });

  const { checkbox: markerMoveCheckbox, isBlockingSubmit: markerMoveBlocking } =
    useMarkerMoveConfirmation({
      originalLatLng: hazard ? { lat: hazard.lat, lng: hazard.lng } : null,
      currentLatLng: { lat, lng },
      isEditing: Boolean(hazard),
    });

  const displayLat = markerLock.stagedLatLng?.lat ?? lat;
  const displayLng = markerLock.stagedLatLng?.lng ?? lng;

  const currentHazard = useMemo(() => {
    return {
      data: { hazardType: type },
      isActive: status === 'active',
      lat: displayLat,
      lng: displayLng,
    } as Partial<Hazard>;
  }, [displayLat, displayLng, status, type]);

  if (isLoadingRegions) {
    return <Loader disableBackground />;
  }

  return (
    <MapFormLayout ref={scrollContainerRef}>
      <MapFormLayout.MapContent>
        <Map
          center={defaultMapCenter}
          zoom={initialZoom}
          mapType={MapType.GenericForm}
          showSearch
          searchDisabled={markerLock.isLocked}
          searchBarAdornment={<MarkerEditControls lock={markerLock} />}
          cursor={markerLock.isLocked ? undefined : 'crosshair'}
          onLocationChange={markerLock.handleLocationChange}
          searchApi="google"
        >
          <AlertCamerasLayer
            visible
            interactive={false}
            withLabels
            selectedCameraId={isMediaAlertCamera(media) ? media.id : undefined}
          />
          <HazardMarker
            hazard={currentHazard}
            onChange={markerLock.handleLocationChange}
            locked={markerLock.isLocked}
          />
          {hazard && (
            <MarkerEditGhost
              markerLock={markerLock}
              original={hazard}
              footprint={iconFootprint(getHazardEventIcon(hazard), 'center')}
            >
              <Map.HazardGeoEventMarker
                geoEvent={hazard}
                draggable={false}
                opacityOverride={0.5}
              />
            </MarkerEditGhost>
          )}
        </Map>
      </MapFormLayout.MapContent>
      <MapFormLayout.FormContent>
        <FormProvider {...formMethods}>
          <FormWithoutEnter
            noValidate
            onSubmit={handleSubmit(handleSubmitHazard)}
          >
            <Stack spacing={2}>
              <Typography variant="h3">
                {t('createEditHazard.sections.hazard')}
              </Typography>

              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => {
                  const { ref, ...muiFieldProps } = field;
                  return (
                    <TextField
                      id="field-control-name"
                      label={t('createEditHazard.inputs.name.label')}
                      fullWidth
                      {...muiFieldProps}
                      inputRef={ref}
                      error={Boolean(fieldState.error)}
                      helperText={
                        fieldState.error?.message ||
                        t('createEditHazard.inputs.name.helperText')
                      }
                      required
                      disabled={formState.isSubmitting}
                      slotProps={{
                        formHelperText: {
                          // @ts-expect-error - for testing purposes
                          'data-testid': 'name-helper-message',
                        },
                      }}
                    />
                  );
                }}
              />

              <Controller
                name="address"
                control={control}
                render={({ field, fieldState }) => {
                  const { ref, ...muiFieldProps } = field;
                  return (
                    <TextField
                      id="field-control-address"
                      label={t('createEditHazard.inputs.address.label')}
                      fullWidth
                      {...muiFieldProps}
                      inputRef={ref}
                      error={Boolean(fieldState.error)}
                      helperText={
                        fieldState.error?.message ||
                        t('createEditHazard.inputs.address.helperText')
                      }
                      required
                      disabled={formState.isSubmitting}
                    />
                  );
                }}
              />

              <Controller
                name="type"
                control={control}
                render={({ field, fieldState }) => {
                  const { ref, ...muiFieldProps } = field;
                  return (
                    <TextField
                      select
                      id="field-control-type"
                      label={t('createEditHazard.inputs.type.label')}
                      fullWidth
                      {...muiFieldProps}
                      value={muiFieldProps.value || ''}
                      inputRef={ref}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      required
                      disabled={formState.isSubmitting}
                      slotProps={{
                        select: {
                          renderValue: (option) =>
                            t(HAZARD_TYPE_TO_TRANS_KEY[option as HazardType]),
                        },
                      }}
                    >
                      {hazardTypes.map(({ transKey, value }) => {
                        const icon = getHazardTypeIcon(value);
                        return (
                          <MenuItem
                            key={value}
                            value={value}
                            className={classes.menuItem}
                          >
                            <Box
                              component="img"
                              src={icon.data}
                              alt=""
                              sx={{ width: 24, height: 24, mr: 1 }}
                            />
                            {t(transKey)}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  );
                }}
              />

              <Controller
                name="parentGeoEvents"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <FormControl fullWidth>
                      <AutocompleteWithBackdrop
                        {...geoEventAutocompleteProps}
                        id="geoEvents"
                        value={field.value}
                        onChange={(_, value) => {
                          field.onChange(value);
                        }}
                        onBlur={field.onBlur}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name={field.name}
                            inputRef={field.ref}
                            variant="outlined"
                            label={t('createEditHazard.inputs.incidents.label')}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                            disabled={formState.isSubmitting}
                            slotProps={{
                              formHelperText: {
                                // @ts-expect-error - for testing purposes
                                'data-testid': 'incidents-helper-message',
                              },
                            }}
                            required
                          />
                        )}
                      />
                    </FormControl>
                  );
                }}
              />

              <Controller
                name="status"
                control={control}
                render={({ field, fieldState }) => {
                  const { ref, ...muiFieldProps } = field;
                  return (
                    <TextField
                      select
                      id="field-control-status"
                      label={t('createEditHazard.inputs.status.label')}
                      fullWidth
                      {...muiFieldProps}
                      inputRef={ref}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      required
                      disabled={formState.isSubmitting}
                    >
                      {statusOptions.map(({ transKey, value }) => {
                        return (
                          <MenuItem
                            key={value}
                            value={value}
                            className={classes.menuItem}
                          >
                            {t(transKey)}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  );
                }}
              />

              {!isInternalTabEnabled && <FormLinks />}

              {!hazard && (
                <>
                  <Box sx={{ marginTop: 1 }}>
                    <Typography variant="h3">
                      {t('createEditHazard.sections.update')}
                    </Typography>
                  </Box>

                  <Controller
                    name="reportNotificationType"
                    control={control}
                    render={({ field, fieldState }): JSX.Element => {
                      const { ref, ...muiFieldProps } = field;
                      return (
                        <TextField
                          select
                          id="field-control-reportNotificationType"
                          label={t(
                            'createEditHazard.inputs.reportNotificationType.label',
                          )}
                          fullWidth
                          {...muiFieldProps}
                          inputRef={ref}
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                          required
                          className={classes.capitalizeLabel}
                          disabled
                        >
                          {[
                            NotificationTypeConstant.NORMAL,
                            NotificationTypeConstant.SILENT,
                          ].map((notifType) => {
                            return (
                              <MenuItem
                                key={notifType}
                                value={notifType}
                                className={classes.capitalizeLabel}
                              >
                                {notifType}
                              </MenuItem>
                            );
                          })}
                        </TextField>
                      );
                    }}
                  />

                  <ReportMessageField
                    name="reportMessage"
                    initialValue={initialValues.reportMessage}
                    currentValues={{
                      notificationType: NotificationType.Normal,
                    }}
                    helperText={t(
                      'createEditHazard.inputs.reportMessage.helperText',
                    )}
                  />

                  <MediaForm
                    latFieldName="mediaLat"
                    lngFieldName="mediaLng"
                    azFieldName="mediaAz"
                    geoEventType={GeoEventTypes.hazard}
                  />
                </>
              )}

              {isInternalTabEnabled && <FormLinks />}

              {markerMoveCheckbox}

              <Button
                fullWidth
                size="large"
                type="submit"
                disabled={
                  formState.isSubmitting ||
                  (!hazard && uploadImgError) ||
                  markerLock.hasStagedChange ||
                  markerMoveBlocking
                }
              >
                {t(`createEditHazard.buttons.${hazard ? 'edit' : 'create'}`)}
              </Button>
            </Stack>
          </FormWithoutEnter>
        </FormProvider>
      </MapFormLayout.FormContent>
    </MapFormLayout>
  );
};
